import React, {
  Dispatch,
  ReactElement, SetStateAction, useCallback, useEffect,
} from 'react';
import {
  DatePicker, FormGroup, Label,
} from 'idsk-react-components';
import { ExpansionPanel, SearchInputButton } from 'slovlex-idsk-react-components';
import './sidePanel.scss';
import { PravoEuFilter } from '../../../model/PravoEuFilter';
import TypSingleSelect from './TypSingleSelect';
import AutorSingleSelect from './AutorSingleSelect';
import { SafeParameterSetter } from '../../../types/SafeParameterSetter';

interface Props {
  filter: PravoEuFilter,
  setFilter: Dispatch<SetStateAction<PravoEuFilter>>,
  onUpdateField: SafeParameterSetter<PravoEuFilter>,
  filterCopy: PravoEuFilter,
  onUpdateFieldCopy: SafeParameterSetter<PravoEuFilter>,
}

const MobileFilters = ({
  filter,
  onUpdateField,
  filterCopy,
  onUpdateFieldCopy,
  setFilter,
}: Props): ReactElement => {
  const submitFilter = useCallback((): void => {
    setFilter(prevState => ({
      ...prevState,
      hladanyVyraz: filterCopy.hladanyVyraz,
      celex: filterCopy.celex,
      rokUverejnenia: filterCopy.rokUverejnenia,
      eurovoc: filterCopy.eurovoc,
      zmluva: filterCopy.zmluva,
      autor: filterCopy.autor,
      type: filterCopy.type,
    }));
  }, [filterCopy.autor,
    filterCopy.type,
    filterCopy.celex,
    filterCopy.eurovoc,
    filterCopy.hladanyVyraz,
    filterCopy.rokUverejnenia,
    filterCopy.zmluva,
    setFilter]);

  function stringToNumber(input: string): number {
    const num = parseInt(input, 10);
    return Number.isNaN(num) ? undefined : num;
  }

  useEffect(() => {
    const keyDownHandler = (e): void => {
      if (e.key === 'Enter') {
        e.preventDefault();
        submitFilter();
      }
    };
    document.addEventListener('keydown', keyDownHandler);
    return () => {
      document.removeEventListener('keydown', keyDownHandler);
    };
  }, [submitFilter]);

  const onUpdateFieldType = (field: string, value: never): void => {
    setFilter((prevState) => ({
      ...prevState,
      [field]: value,
    }));

    if (field === 'polozkyList') {
      onUpdateField('type', value[0]);
      onUpdateFieldCopy('type', value[0]);
    }
  };

  const onUpdateFieldAutor = (field: string, value: never): void => {
    setFilter((prevState) => ({
      ...prevState,
      [field]: value,
    }));

    if (field === 'polozkyList') {
      onUpdateField('autor', value[0]);
      onUpdateFieldCopy('autor', value[0]);
    }
  };

  return (
    <div className="pravo-eu-list-side-panel">
      <ExpansionPanel title="Celex" className="first-expansion-panel">
        <SearchInputButton
          className="slovlex-idsk-side-panel-input-wrapper"
          searchText={filterCopy?.celex}
          setSearchText={(value: string) => onUpdateFieldCopy('celex', value)}
          onButtonSubmit={submitFilter}
          onClearSearch={() => onUpdateFieldCopy('celex', '')}
        />
      </ExpansionPanel>
      <ExpansionPanel title="Rok uverejnenia">
        <SearchInputButton
          className="slovlex-idsk-side-panel-input-wrapper"
          type="search-number"
          maxLength={4}
          searchText={filterCopy?.rokUverejnenia !== undefined ? `${(filterCopy?.rokUverejnenia)}` : ''}
          setSearchText={(value: string) => onUpdateFieldCopy('rokUverejnenia', stringToNumber(value))}
          onButtonSubmit={submitFilter}
          onClearSearch={() => onUpdateFieldCopy('rokUverejnenia', undefined)}
        />
      </ExpansionPanel>
      <ExpansionPanel title="EuroVoc">
        <SearchInputButton
          className="slovlex-idsk-side-panel-input-wrapper"
          searchText={filterCopy?.eurovoc}
          setSearchText={(value: string) => onUpdateFieldCopy('eurovoc', value)}
          onButtonSubmit={submitFilter}
          onClearSearch={() => onUpdateFieldCopy('eurovoc', '')}
        />
      </ExpansionPanel>
      <ExpansionPanel title="Typ">
        <TypSingleSelect
          onUpdateField={onUpdateFieldType}
          onUpdateFieldCopy={onUpdateFieldCopy}
          filter={filter}
          filterCopy={filterCopy}
        />
      </ExpansionPanel>
      <AutorSingleSelect
        onUpdateField={onUpdateFieldAutor}
        onUpdateFieldCopy={onUpdateFieldCopy}
        filter={filter}
        filterCopy={filterCopy}
      />
      <ExpansionPanel title="Zmluva">
        <SearchInputButton
          className="slovlex-idsk-side-panel-input-wrapper"
          searchText={filterCopy?.zmluva}
          setSearchText={(value: string) => onUpdateFieldCopy('zmluva', value)}
          onButtonSubmit={submitFilter}
          onClearSearch={() => onUpdateFieldCopy('zmluva', '')}
        />
      </ExpansionPanel>
      <ExpansionPanel title="Dátum uverejnenia">
        <FormGroup>
          <FormGroup>
            <Label htmlFor="datumUverejneniaOd">Dátum od</Label>
            <DatePicker
              width="full"
              showIcon
              value={filter?.datumUverejneniaOd}
              onChange={date => onUpdateField('datumUverejneniaOd', date)}
              strictParsing
            />
          </FormGroup>
          <FormGroup>
            <Label htmlFor="datumUverejneniaDo">Dátum do</Label>
            <DatePicker
              width="full"
              showIcon
              value={filter?.datumUverejneniaDo}
              onChange={date => onUpdateField('datumUverejneniaDo', date)}
              strictParsing
            />
          </FormGroup>
        </FormGroup>
      </ExpansionPanel>
      <ExpansionPanel title="Dátum schválenia">
        <FormGroup>
          <FormGroup>
            <Label htmlFor="datumUverejneniaOd">Dátum od</Label>
            <DatePicker
              width="full"
              showIcon
              value={filter?.datumSchvaleniaOd}
              onChange={date => onUpdateField('datumSchvaleniaOd', date)}
              strictParsing
            />
          </FormGroup>
          <FormGroup>
            <Label htmlFor="datumSchvaleniaDo">Dátum do</Label>
            <DatePicker
              width="full"
              showIcon
              value={filter?.datumSchvaleniaDo}
              onChange={date => onUpdateField('datumSchvaleniaDo', date)}
              strictParsing
            />
          </FormGroup>
        </FormGroup>
      </ExpansionPanel>
      <ExpansionPanel title="Dátum účinnosti od">
        <FormGroup>
          <FormGroup>
            <Label htmlFor="datumUcinnostiOdOd">Dátum od</Label>
            <DatePicker
              width="full"
              showIcon
              value={filter?.datumUcinnostiOdOd}
              onChange={date => onUpdateField('datumUcinnostiOdOd', date)}
              strictParsing
            />
          </FormGroup>
          <FormGroup>
            <Label htmlFor="datumUcinnostiOdDo">Dátum do</Label>
            <DatePicker
              width="full"
              showIcon
              value={filter?.datumUcinnostiOdDo}
              onChange={date => onUpdateField('datumUcinnostiOdDo', date)}
              strictParsing
            />
          </FormGroup>
        </FormGroup>
      </ExpansionPanel>
      <ExpansionPanel title="Dátum účinnosti do">
        <FormGroup>
          <FormGroup>
            <Label htmlFor="datumUcinnostiDoOd">Dátum od</Label>
            <DatePicker
              width="full"
              showIcon
              value={filter?.datumUcinnostiDoOd}
              onChange={date => onUpdateField('datumUcinnostiDoOd', date)}
              strictParsing
            />
          </FormGroup>
          <FormGroup>
            <Label htmlFor="datumUcinnostiDoDo">Dátum do</Label>
            <DatePicker
              width="full"
              showIcon
              value={filter?.datumUcinnostiDoDo}
              onChange={date => onUpdateField('datumUcinnostiDoDo', date)}
              strictParsing
            />
          </FormGroup>
        </FormGroup>
      </ExpansionPanel>
      <ExpansionPanel title="Dátum uzávierky">
        <FormGroup>
          <FormGroup>
            <Label htmlFor="datumUzavierkyOd">Dátum od</Label>
            <DatePicker
              width="full"
              showIcon
              value={filter?.datumUzavierkyOd}
              onChange={date => onUpdateField('datumUzavierkyOd', date)}
              strictParsing
            />
          </FormGroup>
          <FormGroup>
            <Label htmlFor="datumUzavierkyDo">Dátum do</Label>
            <DatePicker
              width="full"
              showIcon
              value={filter?.datumUzavierkyDo}
              onChange={date => onUpdateField('datumUzavierkyDo', date)}
              strictParsing
            />
          </FormGroup>
        </FormGroup>
      </ExpansionPanel>
      <ExpansionPanel title="Dátum transpozície">
        <FormGroup>
          <FormGroup>
            <Label htmlFor="datumTranspozicieOd">Dátum od</Label>
            <DatePicker
              width="full"
              showIcon
              value={filter?.datumTranspozicieOd}
              onChange={date => onUpdateField('datumTranspozicieOd', date)}
              strictParsing
            />
          </FormGroup>
          <FormGroup>
            <Label htmlFor="datumTranspozicieDo">Dátum do</Label>
            <DatePicker
              name="datumTranspozicieDo"
              width="full"
              showIcon
              value={filter?.datumTranspozicieDo}
              onChange={date => onUpdateField('datumTranspozicieDo', date)}
              strictParsing
            />
          </FormGroup>
        </FormGroup>
      </ExpansionPanel>
    </div>
  );
};

export default MobileFilters;
